import { FC } from "react";
import { TriangleAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import { Dashboard } from "@/models/dashboard/Dashboard";
import { IDENTITY_STYLE } from "../lib/dashboardIdentityStyle";

/** Class for the name input while a clash is shown, so field and warning read as one. */
export const NAME_CLASH_INPUT_CLASS =
  "border-warning focus-visible:border-warning focus-visible:ring-warning/30";

interface DashboardNameClashWarningProps {
  /** null renders the empty live region, which must already be on the page when a clash appears. */
  clash: Dashboard | null;
  /** The audience of the dashboard being named, which decides what the clash risks. */
  isOrganizationDashboard: boolean;
}

/**
 * Inline and non-blocking (LAR-661): a duplicate name may be on purpose, so the dialog keeps its
 * submit button and only relabels it "… alligevel". Render it with every name field, clash or
 * not: a live region is only announced reliably when it exists before its text does, so a screen
 * reader hears the warning the moment the typed name starts to clash.
 */
const DashboardNameClashWarning: FC<DashboardNameClashWarningProps> = ({
  clash,
  isOrganizationDashboard,
}) => (
  <div role="status">
    {clash && (
      <div
        className={cn(
          "flex gap-2.5 rounded-lg border px-3 py-2.5 text-xs leading-relaxed",
          IDENTITY_STYLE["organisation-draft"]
        )}
      >
        <TriangleAlert
          className="mt-px size-4 shrink-0 text-warning"
          aria-hidden="true"
        />
        <span>
          <strong className="font-semibold">
            Organisationen har allerede et dashboard, der hedder "{clash.name}".
          </strong>{" "}
          {isOrganizationDashboard
            ? "To organisationsdashboards med samme navn er svære at skelne fra hinanden for dine kolleger."
            : "Et personligt dashboard med samme navn er let at forveksle med det, og ændringer i dit personlige dashboard ses ikke af dine kolleger."}
        </span>
      </div>
    )}
  </div>
);

export default DashboardNameClashWarning;
